import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BreadCrumb, SnackSimple } from '../../../../components'
import { CRUD, Types } from '../../../../utils/enums'
import { InformativeCard } from '../../../../utils/types'
import { ObjectGeneric, onChangeEvent, Table } from '../../../../utils/types/generals'
import { initialData, initialValues, useMenuItems, input, formatFilterMainZones } from './initialState'
import { getList, handleErrors, submit } from './methods'
import { useStyles } from './style'
import Form from './components/form'
import { generalDisabled } from '../../../../utils/validate/validate'
import { useContextState } from '../../../../hooks/theme'
import { httpFetch } from '../../../../utils/crud'
import { zonesService } from '../../../../service/zones'
import SimpleModal from '../../../../components/modal/simpleModal'
import General from '../../../../components/modules/general'
import { icons } from '../../../../utils/icons'
import { FiltersDrawer } from '../../../../components/filtersDrawer'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { DinamicForm } from '../../../../components/dinamicForm'
import { useFilter } from '../../../../hooks/filter'
import { ModelDownloadButtons } from '../../../../components/donwloadFiles/model'
import { useDownload } from '../../../../hooks/download/useDownload'
import { returnQuery, returnQueryWithPagination } from '../../../../utils/methods/returnQuery'
import { zoneService } from "../../../../utils/constants/callService"
import { Constants } from '../../../../utils/constants'
import { format } from 'date-fns'

const Zones = () => {
  const { t } = useTranslation()
  const { colors } = useContextState()
  const styles = useStyles(colors)
  const [inputValues, setInputValues] = useState(initialValues)
  const [information, setInformation] = useState<InformativeCard | null>(null)
  const [data, setData] = useState<ObjectGeneric>({})
  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const inputs = input;
  const detailToInclude = ['name', 'description', 'createTime', 'zoneInitId']
  const [allErrors, setAllErrors] = useState<any>({})
  const [openModalDelete, setOpenModalDelete] = useState(false)
  const [open, setOpen] = useState(false)
  const [row, setRow] = useState<Table['row']>([])
  const [selected, setSelected] = useState<number>(-1)
  const [crud, setCrud] = useState(false)
  const [drawerDetail, setDrawerDetail] = useState({
    title: '',
    accept: '',
    cancel: '',
  })
  const refTime = useRef<NodeJS.Timeout | null>(null)
  const filterMain = useFilter({ formatFilter: formatFilterMainZones })
  const { counter, errors, filterData, paginationModel, handlePagination, onChange, cleanFilter, sortTable, query, filterString } = filterMain
  const [totalPages, setTotalPages] = useState(0)
  const [parentZones, setParentZones] = useState<{
    label: string;
    value: number | null;
  }[]>([])
  const [loadDetail, setLoadDetail] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })
  const menuList = useMenuItems(styles)
  const [menu, setMenu] = useState<
    {
      label: string
      icon?: any
      value?: any
    }[]
  >(menuList)

  const handleCloseSnackbar = (value: boolean) => setOpenSnackbar({ ...openSnackbar, open: value })

  const crudChange = async (e: onChangeEvent | string): Promise<void> => {
    if (typeof e !== 'string') {
      const { name, value } = e?.target

      let body = {
        ...data,
        [name]: value?.value ?? value,
      }

      setData(body)
      setAllErrors(handleErrors(body, t))
    }
  }

  const handleSubmit = async () => {
    setBtnLoading(true)
    const response = await submit(data, t, information?.id)
    if (!response?.response?.errors) {
      getAllZones()
      getParents()
    }
    setOpenSnackbar(response.snackbarBody)
    setBtnLoading(false)
  }

  const handleInformation = async (e: any, index: number) => {
    setSelected(index)
    openDrawer(true)
    setLoadDetail(true)
    const responseZone = await zonesService(httpFetch).getZoneById(String(index))
    let data: any[] = [
      {
        label: t(`general.tags.name`),
        value: responseZone.name,
        name: 'name',
      },
      {
        label: t(`general.tags.description`),
        value: responseZone.description,
        name: 'description',
      },
      {
        label: t(`general.tags.zoneInitId`),
        value: responseZone.zoneParent?.name,
        name: 'zoneInitId',
        selected: responseZone.zoneInitId,
      },
      {
        label: t(`general.tags.createTime`),
        value: format(new Date(responseZone.createTime), Constants.dateFormatGeneral),
        name: 'createTime',
      },
    ]
    setAllErrors({})

    if (e) {
      setMenu(menuList?.filter((item) => item.value !== e.status?.value))
    }

    setInformation(
      e
        ? {
          data,
          tmpData: data,
          id: e.id,
        }
        : null
    )

    setData(initialData)
    setLoadDetail(false)
  }

  const handleMenu = (item: any) => {
    setBtnLoading(true)
    if (item.value === CRUD.EDIT) {
      setCrud(true)
      let body = {} as ObjectGeneric
      information?.tmpData?.forEach((element) => {
        body = {
          ...body,
          [element.name as string]: element?.selected ?? element.value,
        }
      })
      setInputValues({
        ...initialValues,
        zoneInitId: {
          ...initialValues.zoneInitId,
          items: parentZones
            ?.filter((item) => item.value !== information?.id)
            ?.map((item) => ({
              label: item.label ?? '',
              value: item.value,
            })) ,
        },
      })

      setData(body)
    } else if (item.value === CRUD.DELETE) {
      setOpenModalDelete(true)
    }
    setBtnLoading(false)
  }

  const handleDelete = () => {
    if (information?.id) {
      setBtnLoading(true)
      zonesService(httpFetch)
        .deleteZones(information.id)
        .then((response) => {
          if (!response.errors) {
            getAllZones()
            getParents()
            setOpenSnackbar({
              open: true,
              type: Types.SUCCESS,
              message: t(`zones.snackbar.delete.success`),
            })
          }
        })
        .catch(() => {
          setOpenSnackbar({
            open: true,
            type: Types.ERROR,
            message: t(`zones.snackbar.delete.error`),
          })
        })
        .finally(() => {
          setBtnLoading(false)
          setOpenModalDelete(false)
        })
    }
  }

  const getAllZones = async () => {
    setIsLoading(true)
    const responseList = await getList(colors, t, returnQuery({ ...filterMain.query, filter: filterMain.query.filter?.replaceAll('+', '%2B') }))
    setTotalPages(responseList.totalPages);
    setRow(responseList.row)
    setIsLoading(false)
    setData(initialData)
    setCrud(false)
    setOpen(false)
    setData(initialData)
    setBtnLoading(false)
    setSelected(-1)
  }

  const filterFathersZones = () => {
    setInputValues({
      ...initialValues,
      zoneInitId: {
        ...initialValues.zoneInitId,
        items: parentZones
            ?.map((item) => ({
              label: item.label ?? '',
              value: item.value,
            })) ,
      },
    })
  }

  const getParents = async () => {
    const parentZones = await zonesService(httpFetch).getParentZones()

    const parentFormat = parentZones?.map((item) => ({
      label: item.name ?? '',
      value: item.id,
    })) 

    setParentZones(parentFormat)
  }

  const getData = async () => {
    getAllZones()
  }

  const openDrawer = (edit?: boolean) => {
    setDrawerDetail({
      title: t(`zones.informationCard.${edit ? 'edit' : 'create'}.title`),
      accept: t(`zones.informationCard.buttons.${edit ? 'edit' : 'add'}`),
      cancel: t('userType.informationCard.buttons.cancel'),
    })
    if (!edit) {
      setData(initialData)
    }
    setInformation(null)
    setAllErrors({})
    filterFathersZones()
    setCrud(!edit)
    setOpen(true)
  }

  const closeDrawer = () => {
    setOpen(false)
    setCrud(false)
    setSelected(-1)
  }

  const downloadMain = useDownload({
    docName: t('zones.filter.title'),
    query: returnQueryWithPagination({ filter: filterString, sort: query?.sort, }).replace('&', ''),
    downloadFuntion: zoneService.downloadFile,
  })

  const column: any[] = [
    {
      id: 'name',
      label: t('general.tags.name'),
      sortable: true,
      minWidth: 100

    },
    {
      id: 'description',
      label: t('general.tags.description'),
      sortable: true,
      minWidth: 100

    },
    {
      id: 'createTime',
      label: t('general.tags.createTime'),
      sortable: true,
      minWidth: 100

    },
  ]

  useEffect(() => {
    if (refTime.current) { clearTimeout(refTime.current) }

    refTime.current = setTimeout(() => {
      getData()
    }, 300)
  }, [filterMain.query.all])

  useEffect(() => {
    getParents()
  }, [])
 

  return (
    <>
      <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />

      <SnackSimple
        close={downloadMain.closeOpenSnakbar}
        open={downloadMain.openSnackbar.open}
        type={downloadMain.openSnackbar.type as Types}
        message={downloadMain.openSnackbar.message}
      />

      <SimpleModal
        loadingButton
        isLoading={btnLoading}
        open={openModalDelete}
        close={() => {
          setOpenModalDelete(false)
        }}
        title={t('zones.modal.delete.title')}
        description={''}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        color={colors}
        back={() => {
          setOpenModalDelete(false)
        }}
        next={handleDelete}
      />

      <General
        newHeader={<>
          <BreadCrumb
            removeBreadcrumb={['unit']}
            replaceLabels={{ zone: 'storeZone' }}
          />
          <Box sx={styles.header}>
            <Typography variant='title_grey_big'>{t('zones.filter.title')}</Typography>

            <FiltersDrawer cleanFilter={cleanFilter} count={counter} right={-50} top={-40}>
              <Box sx={styles.section}>
                <DinamicForm errors={errors} input={Object.values(inputs)} values={filterData} onChange={onChange} filter={true} />
              </Box>
            </FiltersDrawer>

            <Box sx={styles.flex}>
              <ModelDownloadButtons
                csv={downloadMain.downloadCSV}
                pdf={downloadMain.downloadPDF}
                word={downloadMain.downloadWORD}
                disableCSV={downloadMain.download.excel}
                disablePDF={downloadMain.download.pdf}
                disableWORD={downloadMain.download.word}
                styleType
              />

              <Tooltip title={t('ToolTips.clearFilters')}>
                <IconButton
                  sx={(styles.clean, (counter || 0) > 0 ? null : styles.hide)}
                  onClick={() => {
                    cleanFilter()
                  }}
                >
                  {icons('clean')}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ToolTips.create')}>
                <IconButton
                  sx={styles.clean}
                  className='add'
                  onClick={() => {
                    openDrawer()
                  }}
                >
                  {icons('add')}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </>}
        open={open}
        rightDrawer={{
          ...information,
          data: information?.data
            ?.filter((item) => detailToInclude.includes(item?.name ?? ''))
            .sort((a: any) => {
              if (a.name === 'name') return -1
              return 0
            }),
          form: <Form btnLoading={btnLoading} data={data} input={Object.values(inputValues)} crudChange={crudChange} allErrors={allErrors} />,
          menuItems: menu,
          crud,
          title: drawerDetail.title,
          accept: {
            title: drawerDetail.accept,
            disabled: generalDisabled(inputValues, data) || btnLoading,
            onClick: handleSubmit,
          },
          cancel: {
            title: drawerDetail.cancel,
            onClick: closeDrawer,
          },
          clickMenu: handleMenu,
          loadindDetail: loadDetail
        }}
        table={{
          column: column,
          row: row,
          title: t('zones.filter.title'),
          onClick: handleInformation,
          loadingTable: isLoading,
          selected,
          serverSidePagination: {
            page: paginationModel.page + 1,
            size: paginationModel.pageSize,
            totalPages: totalPages,
          },
          handleChange: (page: number, pageSize: number) => {
            handlePagination({ page, pageSize })
          },
          sortable: (field, sort, count) => {
            sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          },
          order: query?.sort && {
            count: query?.sort?.count,
            key: query?.sort?.field,
            order: query?.sort?.sort,
          },
        }}
      />
    </>
  )
}

export default Zones